import React from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import { useTheme } from '@adsk/alloy-react-styles';

import {
  useResizePanel,
  UseResizePanelProps,
  useResizeScrollable,
} from '../../hooks';
import Panel, { PanelProps } from '../../Panel';
import { ResizeDivider } from '../../ResizeDivider';
import { Scrollable, ScrollableProps } from '../../Scrollable';

const StyledPanel = styledC(Panel)<{
  minWidth?: number;
  $borderRightColor?: string;
  $boxShadowColor?: string;
}>`
  border-right: 1px solid ${(props) => props.$borderRightColor};
  box-shadow: ${(props) =>
    props.minWidth !== undefined
      ? `0 0 0 1px ${props.$boxShadowColor}`
      : undefined};
  max-width: 100%;
`;

const StyledDiv = styledC.div`
  padding: 0;
  display: flex;
  position: relative;
  max-width: 100%;
`;

export type ResizePanelProps = UseResizePanelProps &
  Omit<PanelProps, 'width' | 'onResize'> & {
    scrollableDivProps?: Omit<
      ScrollableProps,
      'setScrollPosition' | 'storeScrollPosition' | 'children' | 'ref'
    >;
  };

const ResizePanel = React.forwardRef<HTMLDivElement, ResizePanelProps>(
  (
    {
      open,
      width,
      openRight,
      onResize,
      children,
      resizeAffordance = 16,
      minWidth,
      maxWidth,
      closedWidth,
      storageKey,
      style,
      scrollableDivProps,
      ...props
    },
    ref,
  ) => {
    const dynamicTheme = useTheme();
    const resizePanelTokens =
      dynamicTheme.tokens?.component?.panel?.resizePanel;
    const { getResizeDividerProps, getPanelProps } = useResizePanel({
      width,
      minWidth,
      maxWidth,
      openRight,
      onResize,
      open,
      resizeAffordance,
      closedWidth,
      storageKey,
    });
    const {
      setScrollPosition,
      storeScrollPosition,
      ref: scrollableRef,
    } = useResizeScrollable<HTMLDivElement>();
    return (
      <StyledDiv>
        {open && <ResizeDivider {...getResizeDividerProps()} />}
        <StyledPanel
          ref={ref}
          css={style}
          $borderRightColor={resizePanelTokens?.['border-right-color']}
          $boxShadowColor={resizePanelTokens?.['box-shadow-color']}
          {...getPanelProps(props)}
        >
          <Scrollable
            setScrollPosition={setScrollPosition}
            storeScrollPosition={storeScrollPosition}
            ref={scrollableRef}
            {...scrollableDivProps}
          >
            {children}
          </Scrollable>
        </StyledPanel>
      </StyledDiv>
    );
  },
);

ResizePanel.displayName = 'ResizePanel';

ResizePanel.propTypes = {
  /** If panel is open or not */
  open: PropTypes.bool,
  /** If panel is opens to the right or not */
  openRight: PropTypes.bool,
  /** Panel content */
  children: PropTypes.any,
  /** Width of Panel.  Use Panel.WIDTHS for standard widths. When `storageKey` is set, this is only used as the initial value when nothing has been stored yet; later changes to this prop are ignored. */
  width: PropTypes.number,
  /** Width of Resize Affordance */
  resizeAffordance: PropTypes.number,
  /** Called whenever the panel resizes */
  onResize: PropTypes.func,
  /** Minimum width allowed by resize */
  minWidth: PropTypes.number,
  /** Maximum width allowed by resize */
  maxWidth: PropTypes.number,
  /** If panel is closed it will retain this size */
  closedWidth: PropTypes.number,
  /** If provided, persists the panel's width to localStorage under this key so it survives across sessions. A previously stored value takes precedence over `width` on mount; `width` is only used when nothing has been stored yet. */
  storageKey: PropTypes.string,
  /** Props for the scrollable div wrapper */
  scrollableDivProps: PropTypes.object,
};

export default ResizePanel;
